import { Resource, toDateString } from '@nextforce/api'
import { useHeaderHeight } from '@react-navigation/elements'
import { makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { FileFolderDetailsView } from 'file_folder_repository/views/file_folder_details_bottom_sheet'
import moment from 'moment'
import { useCallback } from 'react'
import { TextInput, View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { SelectedShareResourceRecipientsRow } from '../../share_resource_recipients/views'
import { ShareResourceRecipient } from '../../types'

export type AcknowledgementType = 'acknowledgement' | 'signature'
interface ShareResourceDetailsViewProps {
  resource: Resource | undefined
  selectedShareResourceRecipients: ShareResourceRecipient[]
  onPressRemoveRecipient: (recipient: ShareResourceRecipient) => void
  onPressThumbnail: () => void
  body: string
  setBody: (body: string) => void
  acknowledgementType: AcknowledgementType
  setAcknowledgementType: (value: AcknowledgementType) => void
  statement: string
  setStatement: (signature: string) => void
  requestAcknowledgement?: boolean
  dueDate: string
  setDueDate: (date: string) => void
}

export function ShareResourceDetailsView({
  resource,
  selectedShareResourceRecipients,
  onPressRemoveRecipient,
  onPressThumbnail,
  body,
  setBody,
  acknowledgementType,
  setAcknowledgementType,
  setStatement,
  statement,
  dueDate,
  setDueDate,
  requestAcknowledgement = false,
}: ShareResourceDetailsViewProps) {
  const styles = useStyles()
  const headerHeight = useHeaderHeight()

  const onDateChange = useCallback(
    (date: Date) => {
      setDueDate(toDateString(date))
    },
    [setDueDate]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      <KeyboardAwareScrollView
        contentContainerStyle={styles.scrollViewContainer}
        keyboardShouldPersistTaps="handled"
        enableOnAndroid={true}
      >
        <View style={styles.fileFolderDetailsContainer}>
          <FileFolderDetailsView
            itemId={resource?.id}
            itemType={resource?.type}
            onPressThumbnail={onPressThumbnail}
            showActions={false}
            showPath={false}
            showMetadata={false}
          />
        </View>
        {requestAcknowledgement ? (
          <View style={styles.acknowledgementInputContainer}>
            <AcknowledgementTypeInput
              type={acknowledgementType}
              setType={setAcknowledgementType}
              statement={statement}
              setStatement={setStatement}
            />
            <View style={styles.dueDateContainer}>
              <DateTimeChooser
                title={t('dueDate')}
                value={new Date(dueDate)}
                mode="date"
                onChange={onDateChange}
                minDate={moment().add(1, 'days').toDate()}
              />
            </View>
          </View>
        ) : null}
      </KeyboardAwareScrollView>
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={headerHeight}
      >
        <SelectedShareResourceRecipientsRow
          selectedRecipients={selectedShareResourceRecipients}
          onRemoveRecipient={onPressRemoveRecipient}
        />
        <MessageBodyInput
          body={body}
          setBody={setBody}
          autofocus={!requestAcknowledgement}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

const AcknowledgementTypeInput = ({
  type,
  setType,
  setStatement,
  statement,
}: {
  type: AcknowledgementType
  setType: (type: AcknowledgementType) => void
  statement: string
  setStatement: (signature: string) => void
}) => {
  const { theme } = useTheme()
  const styles = useStyles()
  return (
    <View style={styles.container}>
      <Text style={styles.label}>{t('receiptConfirmation')}</Text>
      <View style={styles.acknowledgementTypeInputRow}>
        <AcknowledgementTypeInputItem
          selected={type === 'acknowledgement'}
          title={t('acknowledge')}
          onPress={() => setType('acknowledgement')}
        />
        <AcknowledgementTypeInputItem
          selected={type === 'signature'}
          title={t('sign')}
          onPress={() => setType('signature')}
        />
      </View>
      <View style={styles.statementContainer}>
        <Text style={styles.statementLabel}>
          {t('editStatementForRecipient')}
        </Text>
        <TextInput
          value={statement}
          onChangeText={setStatement}
          multiline
          style={styles.acknowledgementTypeInput}
          cursorColor={theme.colors?.primary}
          selectionColor={theme.colors?.primary}
        />
      </View>
    </View>
  )
}

const AcknowledgementTypeInputItem = ({
  selected,
  title,
  onPress,
}: {
  selected: boolean
  title: string
  onPress?: () => void
}) => {
  const { theme } = useTheme()
  const styles = useStyles()
  return (
    <PressableOpacity
      style={[
        styles.acknowledgementTypeInputItem,
        selected && {
          backgroundColor: theme.colors?.primary,
        },
      ]}
      onPress={onPress}
    >
      <Text
        style={
          selected
            ? { color: 'white', fontWeight: 'bold' }
            : { color: theme.colors?.black }
        }
      >
        {title}
      </Text>
    </PressableOpacity>
  )
}

const MessageBodyInput = ({
  body,
  setBody,
  autofocus,
}: {
  body: string
  setBody: (body: string) => void
  autofocus?: boolean
}) => {
  const { theme } = useTheme()
  const styles = useStyles()
  return (
    <View style={styles.messageBodyContainer}>
      <TextInput
        placeholder={t('typeAMessage')}
        value={body}
        onChangeText={setBody}
        multiline
        style={styles.messageBodyInputContainer}
        cursorColor={theme.colors?.primary}
        selectionColor={theme.colors?.primary}
        autoFocus={autofocus}
        placeholderTextColor={theme.colors?.grey3}
      />
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: { flex: 1 },
  scrollViewContainer: {
    flexGrow: 1,
    paddingBottom: 10,
  },
  thumbnail: {
    width: 150,
    height: 150,
  },
  text: {
    marginTop: 5,
  },
  fileFolderDetailsContainer: {
    marginTop: 10,
  },
  messageBodyContainer: { marginHorizontal: 5, marginBottom: 10 },
  messageBodyInputContainer: {
    borderRadius: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.grey6,
    padding: 10,
    maxHeight: 100,
    color: theme.colors?.black,
  },
  acknowledgementTypeInput: {
    flex: 1,
    borderRadius: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.grey6,
    padding: 10,
    color: theme.colors?.black,
    textAlignVertical: 'top',
  },
  acknowledgementTypeInputItem: {
    padding: 10,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  acknowledgementTypeInputRow: {
    flexDirection: 'row',
    borderRadius: 10,
    marginBottom: 20,
    marginTop: 10,
    overflow: 'hidden',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.grey6,
  },
  dueDateContainer: {
    marginTop: 20,
  },
  acknowledgementInputContainer: {
    marginHorizontal: 15,
    flex: 1,
    marginTop: 10,
  },
  label: { marginBottom: 5, fontSize: 18, fontWeight: 'bold' },
  statementLabel: { marginBottom: 15, fontSize: 18, fontWeight: 'bold' },
  statementContainer: { marginTop: 5, flex: 1 },
}))
